import { DeleteOutlined } from "@ant-design/icons"
import {
  App,
  Button,
  Empty,
  Flex,
  Popconfirm,
  Skeleton,
  Typography
} from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { BulkActionBar } from "../../Files/components/BulkActionBar.tsx"
import { FileViewer } from "../../Files/components/FileViewer.tsx"
import {
  type FileOnlyColumn,
  FilesListView
} from "../../Files/components/FilesListView.tsx"
import { type FileItem } from "../../Files/types/file-item.types.ts"
import { useGlobalFileActions } from "../hooks/useGlobalFileActions.ts"
import { useGlobalTrash } from "../hooks/useGlobalTrash.ts"

const { Text } = Typography

interface GlobalFilesTrashTabProps {
  search: string
}

export function GlobalFilesTrashTab({ search }: GlobalFilesTrashTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)

  const {
    items,
    loading,
    loadingMore,
    failed,
    hasMore,
    loadMore,
    reload,
    isEmpty
  } = useGlobalTrash(search)

  const navigateToProject = (file: FileItem) => {
    const info = file.projectInfo
    if (!info) return
    navigate(
      info.is_project
        ? RoutesPaths.project.view(info.id).overview
        : RoutesPaths.lead.view(info.id)
    )
  }

  const actions = useGlobalFileActions({
    fileItems: items,
    reload,
    onViewProject: navigateToProject
  })

  const clearSelection = () => setSelectedKeys(new Set())
  const selectedFiles = items.filter((file) => selectedKeys.has(file.key))

  const confirmPermanentDelete = (file: FileItem) => {
    modal.confirm({
      title: t("files.permanentDelete.confirmTitle", { name: file.name }),
      content: t("files.permanentDelete.confirmBody"),
      okText: t("files.actions.permanentDelete"),
      okButtonProps: { danger: true },
      onOk: () => actions.permanentlyDeleteFile(file)
    })
  }

  const trashFileActions = (file: FileItem): MenuItemType[] => [
    {
      key: "restore",
      label: t("files.actions.restore"),
      onClick: () => void actions.restoreFile(file)
    },
    {
      key: "permanentDelete",
      danger: true,
      label: t("files.actions.permanentDelete"),
      onClick: () => confirmPermanentDelete(file)
    }
  ]

  const handleBulkRestore = async () => {
    await actions.bulkRestoreFiles(selectedFiles)
    clearSelection()
  }

  const handleBulkPermanentDelete = async () => {
    await actions.bulkPermanentlyDeleteFiles(selectedFiles)
    clearSelection()
  }

  const handleEmptyTrash = async () => {
    await actions.emptyGlobalTrash()
    clearSelection()
  }

  const locationColumn: FileOnlyColumn = {
    title: t("globalFiles.trash.columns.originalLocation", "Original Location"),
    key: "originalLocation",
    renderFile: (file: FileItem) =>
      file.projectInfo ? (
        <Flex vertical gap={0}>
          <span>
            {(file.projectInfo.is_project
              ? t("globalFiles.relatedTo.project", "Project")
              : t("globalFiles.relatedTo.lead", "Lead")) +
              ` · ${file.projectInfo.uid}`}
          </span>
          {file.projectInfo.name && (
            <span className="text-xs text-text-tertiary">
              {file.projectInfo.name}
            </span>
          )}
        </Flex>
      ) : file.isCompanyScoped ? (
        t("globalFiles.location.companyFiles", "Company Files")
      ) : (
        "—"
      )
  }

  return (
    <Flex vertical gap="middle">
      <Flex justify="flex-end">
        <Popconfirm
          title={t("globalFiles.trash.emptyTrash.confirmTitle", "Empty Trash?")}
          description={t(
            "globalFiles.trash.emptyTrash.confirmBody",
            "This will permanently delete all items in Trash that you are authorized to delete. This action cannot be undone."
          )}
          okText={t("files.emptyTrash.confirm")}
          okButtonProps={{ danger: true }}
          onConfirm={handleEmptyTrash}
        >
          <Button danger icon={<DeleteOutlined />}>
            {t("files.emptyTrash.action")}
          </Button>
        </Popconfirm>
      </Flex>

      {selectedFiles.length > 0 && (
        <BulkActionBar
          count={selectedFiles.length}
          canDelete
          permanentDelete
          onRestore={handleBulkRestore}
          onDelete={handleBulkPermanentDelete}
          onClear={clearSelection}
        />
      )}

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : failed ? (
        <Empty
          description={t(
            "globalFiles.trash.loadFailed",
            "Couldn't load Trash."
          )}
        >
          <Button onClick={() => void reload()}>
            {t("common.retry", "Retry")}
          </Button>
        </Empty>
      ) : isEmpty ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={4} align="center">
              <Text strong>
                {t("globalFiles.trash.empty.title", "Trash is empty")}
              </Text>
              <Text type="secondary">
                {t(
                  "globalFiles.trash.empty.description",
                  "Deleted files will appear here before they are permanently removed."
                )}
              </Text>
            </Flex>
          }
        />
      ) : (
        <FilesListView
          items={items}
          actions={trashFileActions}
          onPreview={setPreviewFile}
          selectedKeys={selectedKeys}
          onSelectionChange={(keys) => setSelectedKeys(new Set(keys))}
          trashMode
          extraColumns={[locationColumn]}
          columnConfigMode="global-trash"
        />
      )}

      {!loading && hasMore && (
        <Flex justify="center">
          <Button loading={loadingMore} onClick={loadMore}>
            {t("globalFiles.allFiles.loadMore", "Load more")}
          </Button>
        </Flex>
      )}

      {previewFile && (
        <FileViewer
          file={previewFile}
          items={items}
          onClose={() => setPreviewFile(null)}
          fileActions={trashFileActions}
          isFavorite={() => false}
          onToggleFavorite={() => undefined}
          onViewProject={navigateToProject}
        />
      )}
    </Flex>
  )
}
